import { useState } from "react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { resolveTerms } from "@/shared/utils/resolveTerms";
import type { ResolvedTerm } from "@/shared/utils/resolveTerm";
import { containsKeywordDelimiter, parsePastedKeywords } from "../parsePastedList";
import { groupTerms, type TermEntry, type TermMatches } from "../termMatches";

/** What the field needs from the builder's typed terms — see useTypedTerms. */
export interface TermsModel {
  tickers: string[];
  keywords: string[];
  termMatches: TermMatches;
  addResolvedTerm: (resolved: ResolvedTerm) => void;
  removeTicker: (term: string) => void;
  removeKeyword: (term: string) => void;
  keepAsKeyword: (term: string) => void;
}

interface TermsInputProps {
  terms: TermsModel;
  /** Rendered on the label row, right-aligned — for actions that fill this
   *  same field, like a CSV upload. */
  labelAction?: React.ReactNode;
  errors?: { tickers?: string; keywords?: string };
}

/**
 * One field for everything a watchlist can contain.
 *
 * Whether an entry is a ticker, a keyword or a tag is our storage detail, so
 * the user is no longer asked to choose before they've typed anything. The tag
 * lookup decides: "AAPL", "Apple" and "AAPL.O" all become the same ticker,
 * "OPEC" stays a word but picks up the Energy tag, and anything unrecognised
 * stays a plain keyword — which still matches text on every feed, so nothing
 * is ever lost to a failed lookup.
 */
export default function TermsInput({ terms, labelAction, errors }: TermsInputProps) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const submit = async (raw: string) => {
    const entries = raw
      .split(/[\n,;\t]+/)
      .map((term) => term.trim())
      .filter(Boolean);
    if (entries.length === 0 || busy) return;

    setValue("");
    setBusy(true);
    setProgress(entries.length > 3 ? { done: 0, total: entries.length } : null);
    try {
      const { resolved } = await resolveTerms(entries, {
        onProgress: (done, total) =>
          setProgress(total > 3 ? { done, total } : null),
        // "Mar → MAR" says nothing until it also says Marriott.
        nameTickers: true,
      });
      resolved.forEach(terms.addResolvedTerm);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const groups = groupTerms(terms.tickers, terms.keywords, terms.termMatches);
  const remove = ({ term, kind }: TermEntry) =>
    kind === "ticker" ? terms.removeTicker(term) : terms.removeKeyword(term);

  return (
    <div>
      <div className="flex items-center gap-3">
        <label className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Enter or paste tickers, companies or topics
        </label>
        {labelAction && <span className="ml-auto shrink-0">{labelAction}</span>}
      </div>

      <div className="mt-1 flex gap-1.5">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit(value);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (!containsKeywordDelimiter(text)) return;
            e.preventDefault();
            submit(parsePastedKeywords(text).join("\n"));
          }}
          placeholder="e.g. AAPL, Apple, OPEC"
          disabled={busy}
          className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-100 placeholder:text-gray-600 focus:border-squawk-blue focus:outline-none disabled:opacity-60"
        />
        <button
          onClick={() => submit(value)}
          disabled={busy || !value.trim()}
          className="cursor-pointer rounded bg-gray-700 px-2 py-1.5 text-xs text-gray-300 transition-colors hover:bg-gray-600 disabled:opacity-40"
        >
          <PlusIcon className="size-3.5" />
        </button>
      </div>

      {/* Only while it is actually doing something. The old line explained the
          field to someone already looking at it, and cost a row of permanent
          small print to do it. */}
      {busy && (
        <p className="mt-1 text-xs text-gray-400">
          {progress ? `Checking ${progress.done} of ${progress.total}…` : "Checking…"}
        </p>
      )}

      {/* What the lookup read into the words, first and one per row, so a
          wrong one is quick to find. Most are right — "Apple" is AAPL — but a
          pasted list of words holds first names and months, and "William"
          comes back as William Hill. The note appears with the list, only
          while there is something to check. */}
      {groups.toCheck.length > 0 && (
        <section className="mt-3" aria-label="Matches to check">
          <GroupHeading title="Check these matches" count={groups.toCheck.length} attention />
          <p className="mt-1 text-xs leading-relaxed text-gray-400">
            Words can match a company or topic by coincidence: “William” can pick up William
            Hill. If a match is wrong, click <span className="text-gray-200">Keep as keyword</span>.
            The word stays in your list and the match is removed.
          </p>
          <ul className="mt-2 max-h-64 divide-y divide-gray-700/50 overflow-y-auto rounded border border-gray-700/60 bg-gray-800/40 scrollbar-thin">
            {groups.toCheck.map((entry) => (
              <MatchRow
                key={`${entry.kind}-${entry.term}`}
                entry={entry}
                onKeep={() => terms.keepAsKeyword(entry.term)}
                onRemove={() => remove(entry)}
              />
            ))}
          </ul>
        </section>
      )}

      {groups.tickers.length > 0 && (
        <ChipGroup
          title="Tickers"
          entries={groups.tickers}
          onKeep={(entry) => terms.keepAsKeyword(entry.term)}
          onRemove={remove}
        />
      )}
      {groups.keywords.length > 0 && (
        <ChipGroup
          title="Keywords"
          entries={groups.keywords}
          onKeep={(entry) => terms.keepAsKeyword(entry.term)}
          onRemove={remove}
        />
      )}

      {errors?.tickers && (
        <p className="mt-1 text-xs text-red-400">{errors.tickers}</p>
      )}
      {errors?.keywords && (
        <p className="mt-1 text-xs text-red-400">{errors.keywords}</p>
      )}
    </div>
  );
}

function GroupHeading({
  title,
  count,
  attention = false,
}: {
  title: string;
  count: number;
  attention?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <h4
        className={clsx(
          "text-xs font-medium uppercase tracking-wide",
          attention ? "text-amber-400" : "text-gray-500"
        )}
      >
        {title}
      </h4>
      <span className="font-mono text-2xs tabular-nums text-gray-500">{count}</span>
    </div>
  );
}

/**
 * One match, spelled out: what was typed, and what the lookup made of it.
 *
 * A ticker made from a word shows the word it replaced — the entry no longer
 * matches that word, which is why a wrong one matters. A keyword shows itself
 * and the topic it now also finds.
 */
function MatchRow({
  entry: { term, kind, match },
  onKeep,
  onRemove,
}: {
  entry: TermEntry;
  onKeep: () => void;
  onRemove: () => void;
}) {
  const replaced = kind === "ticker";
  const typed = replaced ? (match?.typed ?? term) : term;
  const label = match?.label;

  return (
    <li
      className="flex items-center gap-2 py-1 pl-2.5 pr-1.5 text-xs"
      title={
        replaced
          ? `Saved as the ticker ${term}${label ? ` (${label})` : ""}. “${typed}” itself isn't searched for.`
          : `Finds “${term}”, and also stories tagged ${label ?? "with this topic"}.`
      }
    >
      <span className="min-w-0 max-w-[40%] shrink-0 truncate text-gray-200">{typed}</span>
      <span className="shrink-0 text-gray-500" aria-hidden>
        {replaced ? "→" : "+"}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        {replaced ? (
          <>
            <span className="shrink-0 font-mono text-blue-300">{term}</span>
            {label && label.toLowerCase() !== term.toLowerCase() && (
              <span className="truncate text-gray-400">{label}</span>
            )}
          </>
        ) : (
          <span className="truncate text-squawk-blue">{label ?? "a related topic"}</span>
        )}
      </span>
      <button
        type="button"
        onClick={onKeep}
        aria-label={`Keep “${typed}” as a keyword`}
        className="shrink-0 cursor-pointer rounded border border-gray-600 bg-transparent px-1.5 py-0.5 text-2xs text-gray-300 transition-colors hover:border-purple-400/60 hover:text-purple-200"
      >
        Keep as keyword
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${typed}`}
        className="shrink-0 cursor-pointer border-none bg-transparent p-0.5 text-gray-500 transition-colors hover:text-gray-200"
      >
        <XMarkIcon className="size-3" />
      </button>
    </li>
  );
}

function ChipGroup({
  title,
  entries,
  onKeep,
  onRemove,
}: {
  title: string;
  entries: TermEntry[];
  onKeep: (entry: TermEntry) => void;
  onRemove: (entry: TermEntry) => void;
}) {
  return (
    <div className="mt-3">
      <GroupHeading title={title} count={entries.length} />
      <div className="mt-1.5 flex max-h-48 flex-wrap gap-1 overflow-y-auto scrollbar-thin">
        {entries.map((entry) => {
          const { term, kind, match } = entry;
          return (
            <span
              key={`${kind}-${term}`}
              className={clsx(
                "inline-flex items-center gap-1 rounded border py-0.5 pl-2 pr-1 text-xs",
                kind === "ticker"
                  ? "border-blue-800/30 bg-blue-900/20 text-blue-300"
                  : "border-purple-800/30 bg-purple-900/20 text-purple-300"
              )}
            >
              {term}

              {/* Typed as the symbol, so not listed for checking. A word typed
                  in capitals can still be a real ticker — GOLD, KEY — so the
                  tick undoes the match as well as showing it. */}
              {match && kind === "ticker" && (
                <button
                  type="button"
                  onClick={() => onKeep(entry)}
                  title={`Matched to ${match.label && match.label !== term ? match.label : "a Newsquawk tag"}, so it finds this company even when the ticker isn't written. Meant the word? Click to keep “${term}” as a keyword.`}
                  aria-label={`Keep ${term} as a keyword`}
                  className="cursor-pointer rounded-sm border-none bg-green-500/15 px-1 text-[9px] font-semibold text-green-400 transition-colors hover:bg-purple-500/20 hover:text-purple-200"
                >
                  ✓
                </button>
              )}

              <button
                type="button"
                onClick={() => onRemove(entry)}
                aria-label={`Remove ${term}`}
                className={clsx(
                  "cursor-pointer border-none bg-transparent p-0.5",
                  kind === "ticker"
                    ? "text-blue-400 hover:text-blue-200"
                    : "text-purple-400 hover:text-purple-200"
                )}
              >
                <XMarkIcon className="size-2.5" />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
